import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ScrollDownArrow from "./ScrollDownArrow";

export default function HeroSection() {
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const rect = el.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          if (rect.bottom > 0 && rect.top < viewportHeight) {
            const progress = Math.min(1, Math.max(0, 1 - (rect.bottom / (viewportHeight + rect.height))));
            const y = progress * 80; // 80px max shift
            el.style.transform = `translateY(${y}px)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="relative min-h-[600px] md:min-h-[720px] flex items-center overflow-hidden">
      {/* Background Image with Parallax */}
      <div ref={bgRef} className="absolute inset-[-40px] will-change-transform">
        <img
          src="https://readdy.ai/api/search-image?query=A%20stunning%20dark%20and%20luxurious%20abstract%20composition%20featuring%20flowing%20golden%20light%20streams%20on%20a%20deep%20black%20background%2C%20with%20subtle%20geometric%20patterns%20and%20metallic%20gold%20particles%20floating%20elegantly%2C%20high-end%20premium%20visual%20suitable%20for%20a%20professional%20insurance%20brand%20hero%20banner%2C%20sophisticated%20and%20refined%20atmosphere%2C%20minimal%20but%20impactful%2C%204K%20quality&width=1920&height=1080&seq=101&orientation=landscape"
          alt="保險門神 Hero 背景"
          className="w-full h-full object-cover object-center scale-105"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
      </div>

      <div className="container-main relative z-10 pt-24 md:pt-28">
        <div className="max-w-[720px]">
          {/* Tag */}
          <div className="inline-block border border-brand-border rounded-full px-5 py-2 mb-6">
            <span className="text-brand-gold text-[1rem] font-medium tracking-wide">
              專業財務顧問
            </span>
          </div>

          {/* Title */}
          <h1 className="text-white font-display font-bold text-[2rem] md:text-[2.75rem] leading-[1.3] md:leading-[1.25] mb-6">
            先守護，再成長。
          </h1>

          {/* Subtitle */}
          <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] mb-6 max-w-[640px]">
            從個人與家庭，到企業與傳承——我們用專業把關，讓保障有效、資產穩健、企業長久。
          </p>

          {/* Supporting text */}
          <p className="text-brand-text-secondary/70 text-[1.0625rem] leading-[1.75] mb-10 max-w-[600px]">
            保險門神 Insurance Guardian，不只是協助你買保險，而是從保單、財產、人生與企業四個面向，建立完整的風險防線與長期規劃。
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://lin.ee/o51JBHX"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-8 py-3.5 rounded-lg text-[1.0625rem] font-semibold hover:bg-brand-gold-soft transition-colors whitespace-nowrap"
            >
              <i className="ri-line-fill" />
              免費預約健診
            </a>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 border border-brand-border text-white px-8 py-3.5 rounded-lg text-[1.0625rem] font-medium hover:border-brand-gold hover:text-brand-gold transition-colors whitespace-nowrap"
            >
              了解四大門神服務
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>

      <ScrollDownArrow />
    </section>
  );
}
